// Card 86akqxu0h: lógica condicional do formulário de pesquisa, para esconder
// e mostrar perguntas enquanto a pessoa responde. É o mesmo algoritmo de
// src/modules/domain/surveys/surveyLogic.ts da API -- quem decide de verdade
// é o servidor, que recalcula o que estava visível e descarta o resto. Se
// mudar um, mude o outro.
import i18n from "@/plugins/i18n";

export const SURVEY_END = "end";

const CHOICE_TYPES = ["single_choice", "multiple_choice", "dropdown"];

export function isChoice(type) {
  return CHOICE_TYPES.includes(type);
}

export function isAnswered(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    return Object.values(value).some((item) => isAnswered(item));
  }
  return false;
}

export function testRule(question, operator, expected, answer) {
  const answered = isAnswered(answer);
  if (operator === "answered") return answered;
  if (operator === "not_answered") return !answered;
  if (!question || !answered) return false;

  if (question.type === "multiple_choice") {
    const picked = Array.isArray(answer) ? answer.map(String) : [];
    const has = picked.includes(String(expected));
    return operator === "not_equals" ? !has : has;
  }

  if (question.type === "scale") {
    const value = Number(answer);
    const target = Number(expected);
    if (operator === "equals") return value === target;
    if (operator === "not_equals") return value !== target;
    if (operator === "gte") return value >= target;
    if (operator === "lte") return value <= target;
    return false;
  }

  if (operator === "equals") return String(answer) === String(expected);
  if (operator === "not_equals") return String(answer) !== String(expected);
  return false;
}

// Onde um salto cai: na própria pergunta ou, quando o destino é uma seção,
// na primeira pergunta dela. -1 = destino inexistente.
export function jumpTargetIndex(questions = [], goTo) {
  const direct = questions.findIndex((question) => question.id === goTo);
  if (direct >= 0) return direct;
  return questions.findIndex((question) => question.sectionId === goTo);
}

// Percorre na ordem: condição que não bate esconde a pergunta; resposta que
// casa com um salto pula as do meio (ou encerra). Resposta de pergunta
// escondida não conta para nada.
export function visibleQuestionIds(questions = [], answers = {}) {
  const byId = new Map(questions.map((question) => [question.id, question]));
  const visible = [];
  const effective = (id) => (visible.includes(id) ? answers[id] : undefined);

  let index = 0;
  while (index < questions.length) {
    const question = questions[index];
    const conditions = question.showIf?.conditions || [];
    const results = conditions.map((condition) =>
      testRule(
        byId.get(condition.questionId),
        condition.operator,
        condition.value,
        effective(condition.questionId)
      )
    );
    const shown =
      !results.length ||
      (question.showIf?.match === "any"
        ? results.some(Boolean)
        : results.every(Boolean));

    if (shown) {
      visible.push(question.id);
      const jump = (question.jumps || []).find((rule) =>
        testRule(question, rule.operator, rule.value, answers[question.id])
      );

      if (jump) {
        if (jump.goTo === SURVEY_END) break;
        const target = jumpTargetIndex(questions, jump.goTo);
        if (target > index) {
          index = target;
          continue;
        }
      }
    }

    index += 1;
  }

  return visible;
}

export function isValidPhone(value) {
  if (!/^[\d\s()+.-]+$/.test(value)) return false;
  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

// Conferência antes de enviar, para a pessoa corrigir sem ida ao servidor.
// O servidor refaz tudo.
export function checkAnswers(questions = [], answers = {}) {
  const visible = visibleQuestionIds(questions, answers);
  const errors = {};

  questions.forEach((question) => {
    if (!visible.includes(question.id)) return;
    const value = answers[question.id];

    if (question.type === "matrix" && question.required) {
      const rows = question.matrix?.rows || [];
      if (rows.some((row) => !isAnswered(value?.[row.id]))) {
        errors[question.id] = i18n.t("common.answerAllRows");
      }
      return;
    }

    if (!isAnswered(value)) {
      if (question.required) {
        errors[question.id] = i18n.t("common.requiredAnswer");
      }
      return;
    }

    if (question.type === "phone" && !isValidPhone(String(value).trim())) {
      errors[question.id] = i18n.t("common.invalidPhone");
    }
  });

  return { visible, errors };
}

// Só o que está visível vai no envio.
export function visibleAnswers(questions = [], answers = {}) {
  const visible = visibleQuestionIds(questions, answers);
  return visible.reduce((acc, id) => {
    if (isAnswered(answers[id])) acc[id] = answers[id];
    return acc;
  }, {});
}

// Retorno de 12/10: seções = páginas. Sem seções, o formulário inteiro é uma
// página (id null). Com seções, só entram as que têm alguma pergunta
// visível para as respostas atuais -- o salto para uma seção adiante faz as
// do meio sumirem da navegação.
export function surveyPages(sections = [], questions = [], answers = {}) {
  if (!sections.length) return [{ id: null, title: "", description: "" }];
  const visible = visibleQuestionIds(questions, answers);
  return sections.filter((section) =>
    questions.some(
      (question) =>
        question.sectionId === section.id && visible.includes(question.id)
    )
  );
}

// Perguntas de uma página (seção); null = todas.
export function questionsOfPage(questions = [], sectionId = null) {
  return sectionId === null
    ? questions
    : questions.filter((question) => question.sectionId === sectionId);
}
